'use client'
import { memo, type RefObject, useMemo, type FC,  Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import type { TypeCmsProductDetail } from '@/schema'
import { defaultGrid, getP18, innerGrid, mq, vw } from '@/styles'
import { Img, SliderFlickity } from './commons'


type Props = {
  data: {
    specs: TypeCmsProductDetail['specifications']
    colors: TypeCmsProductDetail['colors']
    displayAvailableColorsTitle: TypeCmsProductDetail['display_available_colors']
		blueprints: TypeCmsProductDetail['models'][0]['plans']
  }
  ref: RefObject<HTMLElement>
  setColorSelected: Dispatch<SetStateAction<number | null | string>>

}

const dedupeByName = (arr: TypeCmsProductDetail['colors']): TypeCmsProductDetail['colors'] => {
  const seen = new Set<string>()
  return arr.filter(({ name }) => {
    if (seen.has(name)) return false
    seen.add(name)
    return true
  })
}

const getItems = (data: TypeCmsProductDetail['specifications']) => data.map((item, idx) => {
  return (
    <div key={idx} {...item.values?.length > 1 && { className: 'multi' }}>
      <dt>{item.type}</dt>
      {item.values.map((val, index) => <dd key={`${index}-${val.type}`}>
        {val.type === 'media' && val.media && <Img media={val.media.image} />}
        {val.type === 'text' && val.value}
      </dd>)}
    </div>
  )
})

const headingId = 'specifications'
export const SectionSpecifications: FC<Props> = memo(({ data, ref, setColorSelected }) => {
  const { displayAvailableColorsTitle, specs, colors, blueprints } = data

  const { t } = useTranslator()
  const SpecsItems = useMemo(() => getItems(specs), [specs])

  return (
    <Section ref={ref} aria-labelledby={headingId}>
      <h2 id={headingId}>{t('project.product.specs_family_title')}</h2>
      <dl className='l--specs'>
        {SpecsItems}
      </dl>
			<div className='colors-and-blueprints'>
				<dl className='l--colors'>
					<dt {...!displayAvailableColorsTitle && { className: 'srt-only' }}>{t('project.product.available_color')}</dt>
					{dedupeByName(colors).map((item, idx) => (
						<dd key={idx}>
							<Button $colors={item.colors} onClick={() => {
								setColorSelected(item.ids.join(','))
							}}>
								<span>
									{item.name?.split('(')?.[0]}{item.name?.includes('(') ? <><br />({item.name?.split('(')?.[1]}</> : ''}
								</span>
							</Button>
						</dd>
					))}
				</dl>
				{blueprints?.length > 0 && <div className='blueprints-per-model'>
					<SliderFlickity className='slider--blueprints' dynamicSlider={true} reloadOnUpdate={true} hasCustomDots={false} colorDots='#3C3C3B' options={{
						cellAlign: 'left',
						contain: true,
						draggable: blueprints?.length > 1,
						freeScroll: false,
						imagesLoaded: true,
						pageDots: blueprints?.length > 1,
						wrapAround: false,
					}}>
						{blueprints.map((blueprint, idx) => (
							<div className='item--bp' key={idx}>
								<Img media={blueprint.image} />
							</div>
						))}
					</SliderFlickity>
				</div>}
			</div>
    </Section>
  )
})

const Section = styled.section`
  align-items: flex-start;
  grid-column: 1 / span 6;
  height: fit-content;
  margin-bottom: 0;
  padding: 90px 0 0;

  ${mq.greaterThan('tablet')} {
    ${innerGrid(defaultGrid.tablet)}
    grid-column: 1 / span 12;
    grid-row: 1 / span 1;
    padding: 110px 0 0;
    width: 100%;
  }

  ${mq.greaterThan('desktop')} {
    ${innerGrid(defaultGrid.desktop)}
  }

  h2,
  dt,
  dd {
    ${getP18()}
  }

  h2 {
    font-weight: bold;
    grid-column: 1 / span 6;
    margin-bottom: ${vw(20, 'mobile')};
    text-align: left;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 30px;
    }
  }

  .l--specs {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px 6px;
    grid-column: 1 / span 6;
    margin-bottom: 40px;

    ${mq.greaterThan('tablet')} {
      grid-template-columns: 1fr 1fr;
      grid-column: 1 / span 4;
      margin-bottom: auto;
    }

    > div {
      align-content: flex-start;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 3px;
      width: 100%;

      &.multi {
        dd {
          &:not(:last-child) {
            &:after {
              content: ' / ';
            }
          }
        }
      }
    }

    dt {
      opacity: .5;

      ${mq.greaterThan('tablet')} {
        width: 100%;
      }
    }

    dd {
      white-space: nowrap;

      &:first-of-type {
        margin-left: auto;

        ${mq.greaterThan('tablet')} {
          margin-left: 0;
          width: auto;
          width: fit-content;
        }
      }
    }
  }

	.colors-and-blueprints {
		display: flex;
		flex-direction: column;
		gap: 20px;
		margin-bottom: 20px;

		${mq.greaterThan('tablet')} {
			gap: 30px;
			grid-column: 5 / span 4;
			margin-bottom: 0;
		}
	}

  .l--colors {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
    list-style-type: none;

    dt {
      width: 100%;
    }
  }

	.blueprints-per-model {
		overflow: hidden;
		padding: 0 16px;
		width: 100%;

		${mq.greaterThan('tablet')} {
      grid-column: 5 / span 4;
    }

		.flickity-viewport {
			overflow: hidden;
			width: 100%;
		}

		.flickity-slider .flickity-cell {
			margin-right: 0;
			max-width: none;
			width: 100% !important;
		}
	}

	.item--bp {
		display: block;
		width: 100%;
	}

	.item--bp img {
		display: block;
		height: auto;
		max-height: 520px;
		min-width: 0;
		object-fit: contain;
		width: 100%;
	}

	.flickity-page-dots {
		border-top: 1px solid var(--color-navbar-link);
		display: flex;
		gap: 10px;
		justify-content: flex-start;
		margin-top: 16px;
		padding-top: 16px;
		width: 100%;
	}

	.flickity-page-dots button {
		background: var(--color-grey_dark);
		border-radius: 50%;
		height: 10px;
		width: 10px;
		overflow: hidden;
		color: transparent;

		&.is-selected {
			background: var(--color-navbar-link);
		}
	}
`

const generateConicGradient = (colors: string[]) => {
  const slice = 100 / colors.length
  let current = 0

  const arrReversed = colors.length === 2 ? [...colors].reverse() : [...colors]

  const parts = arrReversed.map(color => {
    const part = `${color} ${current}% ${current + slice}%`
    current += slice
    return part
  })

  return `conic-gradient(${parts.join(', ')})`
}

const Button = styled.button<{ $colors: string[] }>`
  border-radius: 50%;
  height: 48px;
  opacity: 1;
  position: relative;
  width: 48px;


  ${mq.greaterThan('tablet')} {
    height: 35px;
    width: 35px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: transparent;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    right: 1px;
    bottom: 1px;
    border-radius: 50%;
    border: 1px solid var(--color-text);
    background: ${({ $colors }) => generateConicGradient($colors)};
    z-index: 1;
  }

  &:focus {
    &::after {
      opacity: .5;
      transition: opacity 100ms ease-in;

    }
  }

  &:hover {
    span {
      background-color: var(--color-background);
      backdrop-filter: blur(10px);
      opacity: 1;
      pointer-events: auto;
      user-select: auto;
    }
  }

  span {
    backdrop-filter: blur(10px);
    background-color: transparent;
    bottom: 0;
    display: block;
    left: 50%;
    min-width: 120px;
    opacity: 0;
    padding: 10px;
    pointer-events: none;
    position: absolute;
    text-align: center;
    transform: translate(-50%, 100%);
    transition: 250ms all ease-out;
    user-select: none;
    width: auto;
    width: fit-content;
    z-index: 10;
  }
`
